import { useCallback, useRef, useState } from "react";

import type { SlashCommandType } from "../DocumentView/DocumentContext";

/** Filter + rank slash-menu types by the query (name startsWith, then includes). */
export function filterSlashTypes(query: string, types: SlashCommandType[]) {
  const q = query.trim().toLowerCase();
  if (!q) return types;
  const starts: SlashCommandType[] = [];
  const contains: SlashCommandType[] = [];
  for (const t of types) {
    const name = t.name.toLowerCase();
    if (name.startsWith(q)) starts.push(t);
    else if (name.includes(q)) contains.push(t);
  }
  return [...starts, ...contains];
}

export interface SlashState {
  items: SlashCommandType[];
  index: number;
  position: { top: number; left: number };
}

/**
 * Shared `/` slash-command menu for page-document text surfaces. Any text-based
 * block (the TipTap rich-text editor, a heading's contentEditable, …) calls
 * `refresh(text, fallbackRect)` whenever its text changes — the menu opens while
 * the text starts with `/` — routes navigation keys through `onKeyDown` while
 * open, and renders `<SlashCommandMenu>` from `slash`. Picking an item calls
 * the host's `onConvert`, which swaps the block type (clearing the `/query`).
 */
export function useSlashMenu(
  slashTypes: SlashCommandType[] | undefined,
  onConvert: ((targetTypeId: string) => void) | undefined,
) {
  const [slash, setSlash] = useState<SlashState | null>(null);
  const slashRef = useRef(slash);
  slashRef.current = slash;
  const typesRef = useRef(slashTypes);
  typesRef.current = slashTypes;
  const onConvertRef = useRef(onConvert);
  onConvertRef.current = onConvert;

  const select = useCallback((item: SlashCommandType) => {
    setSlash(null);
    onConvertRef.current?.(item.typeId);
  }, []);

  const close = useCallback(() => setSlash(null), []);

  // Ref-backed so callers inside long-lived closures (e.g. TipTap's editorProps)
  // read the live open state, not a stale render's.
  const isOpen = useCallback(() => slashRef.current != null, []);

  const setIndex = useCallback((index: number) => {
    setSlash((s) => (s ? { ...s, index } : s));
  }, []);

  // Recompute the menu from the block's current text: open when it starts with
  // `/`, anchored under the caret (or `fallbackRect` when the caret has no box).
  const refresh = useCallback(
    (text: string, fallbackRect?: () => DOMRect | null) => {
      const types = typesRef.current;
      if (!types || !onConvertRef.current) {
        setSlash(null);
        return;
      }
      if (!text.startsWith("/")) {
        setSlash(null);
        return;
      }
      const items = filterSlashTypes(text.slice(1), types);
      const sel = window.getSelection();
      const caret =
        sel && sel.rangeCount > 0
          ? sel.getRangeAt(0).getBoundingClientRect()
          : null;
      const rect =
        caret && (caret.top || caret.left) ? caret : fallbackRect?.() ?? null;
      const position = rect
        ? { top: rect.bottom + 6, left: rect.left }
        : { top: 0, left: 0 };
      setSlash((prev) => ({
        items,
        index: prev ? Math.min(prev.index, Math.max(items.length - 1, 0)) : 0,
        position,
      }));
    },
    [],
  );

  // Returns true when the key was consumed by the open menu — the caller should
  // then preventDefault and skip its own handling of that key.
  const onKeyDown = useCallback(
    (key: string): boolean => {
      const s = slashRef.current;
      if (!s || s.items.length === 0) return false;
      if (key === "ArrowDown") {
        setSlash({ ...s, index: (s.index + 1) % s.items.length });
        return true;
      }
      if (key === "ArrowUp") {
        setSlash({
          ...s,
          index: (s.index - 1 + s.items.length) % s.items.length,
        });
        return true;
      }
      if (key === "Enter" || key === "Tab") {
        select(s.items[s.index]);
        return true;
      }
      if (key === "Escape") {
        setSlash(null);
        return true;
      }
      return false;
    },
    [select],
  );

  return { slash, refresh, onKeyDown, select, close, isOpen, setIndex };
}
